import { refId } from "@suse-o11y/ref-id";
import { useComputed } from "@suse-o11y/signal";
import type { FormList } from "@suse-o11y/signal/form";
import React from "react";

import { Alert } from "~/components/alert";
import { For } from "~/components/for";
import { FormLabel } from "~/components/form/label";
import { IconDelete } from "~/components/icon";
import { max } from "~/tools/max";
import { sortBy } from "~/tools/sort-by";
import { splice } from "~/tools/splice";

import { WidgetColor } from "../../../widget-form/color";
import type { FormState as ColorFormState } from "../../../widget-form/color/form-state";
import { PRESET as COLORS_PRESET } from "../../../widget-form/color/settings";
import { WidgetThresholdsMode, WidgetThresholdsStepValue } from "../../../widget-form/thresholds";
import { ModeFormState, StepFormState } from "../../../widget-form/thresholds/form-state";
import type { Mode } from "../../../widget-form/thresholds/settings";
import { SettingsSectionFormField } from "../_field";
import { SettingsSectionGrid } from "../_section-grid";

function getPresetColor(index: number): string {
	return COLORS_PRESET[index % COLORS_PRESET.length];
}

export interface ThresholdsProps {
	defaultColor?: ColorFormState;
	mode?: ModeFormState;
	thresholds: FormList<StepFormState>;
}

const DEFAULT_MODE = ModeFormState.init("absolute" satisfies Mode);

export const Thresholds: React.FC<ThresholdsProps> = ({ defaultColor, mode, thresholds }) => {
	const steps = useComputed((monitor) => thresholds.getElements(monitor), [thresholds]);

	return (
		<div className="space-y-4">
			<SettingsSectionGrid className="grid-cols-[minmax(80px,auto)_1fr]">
				{steps.length > 0 && (
					<SettingsSectionFormField layout="horizontal" state={mode ?? DEFAULT_MODE}>
						<FormLabel>Mode</FormLabel>
						<WidgetThresholdsMode
							disabled={mode == null}
							mode={mode ?? DEFAULT_MODE}
							name="dashboard-widget-thresholds-mode"
						/>
					</SettingsSectionFormField>
				)}

				<For
					className="col-span-full grid grid-cols-subgrid gap-y-2"
					each={steps}
					empty={
						!defaultColor && <Alert className="col-span-full">No thresholds are configured for this widget yet.</Alert>
					}
				>
					{(threshold, index, arr) => (
						<SettingsSectionFormField key={refId(threshold)} layout="horizontal" state={threshold}>
							<FormLabel>Threshold {arr.length - index}</FormLabel>

							<div className="flex flex-row items-center gap-2">
								<SettingsSectionFormField state={threshold.fields.color}>
									<WidgetColor color={threshold.fields.color} />
								</SettingsSectionFormField>

								<SettingsSectionFormField className="flex-1" layout="horizontal" state={threshold.fields.value}>
									{(fieldMonitor, field) => (
										<WidgetThresholdsStepValue
											name={`dashboard-widget-threshold-${index}-value`}
											onBlur={() => {
												if (field.isDirty(fieldMonitor)) {
													thresholds.setElements((current, monitor) =>
														sortBy(current, [({ fields }) => fields.value.getOutput(monitor) ?? 0, "desc"]),
													);
												}
											}}
											value={field}
										/>
									)}
								</SettingsSectionFormField>

								<button
									className="button button-tertiary button-square"
									onClick={() => {
										thresholds.setInput(splice(index, 1, []));
									}}
									type="button"
								>
									<IconDelete className="size-3" />
								</button>
							</div>
						</SettingsSectionFormField>
					)}
				</For>

				{defaultColor && (
					<SettingsSectionFormField layout="horizontal" state={defaultColor}>
						<FormLabel>Default</FormLabel>
						<WidgetColor color={defaultColor} />
					</SettingsSectionFormField>
				)}
			</SettingsSectionGrid>

			<button
				className="button button-secondary w-full uppercase"
				onClick={() => {
					thresholds.setElements((current, monitor) => {
						const maxElementsValue = max(current.map((threshold) => threshold.fields.value.getOutput(monitor))) ?? 0;

						return [
							StepFormState.init({
								color: getPresetColor(current.length),
								value: (maxElementsValue + 10).toString(),
							}),
							...current,
						];
					});
				}}
				type="button"
			>
				Add threshold
			</button>
		</div>
	);
};
